/**
 * Compact navigation mode switcher rendered in the TopBar.
 * Lets the user toggle between ISA-95 / Module / Persona / Industry views
 * and (when in persona/industry mode) pick the active persona/industry.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers3, Boxes, User, Building2, ChevronDown } from 'lucide-react';
import { Button } from '@tyndo/ui-kit';
import { INDUSTRY_BUNDLES, PERSONA_WORKSPACES } from '@tyndo/types';
import type { IndustryTag, PersonaTag } from '@tyndo/types';
import { useNavMode, type NavMode } from './NavModeContext';

const MODE_META: Record<NavMode, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  isa95: { label: 'ISA-95 layers', icon: Layers3 },
  module: { label: 'Modules', icon: Boxes },
  persona: { label: 'Persona', icon: User },
  industry: { label: 'Industry', icon: Building2 },
};

export default function NavModeSwitcher() {
  const { mode, setMode, persona, setPersona, industry, setIndustry } = useNavMode();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  /**
   * Picking a persona both narrows the sidebar AND opens that persona's
   * workspace. `defaultLandingRoute` is what the manifest has always declared
   * this control should do; until `/workspace/:persona` existed there was
   * nowhere for it to lead, so the field went unread and the switcher only ever
   * filtered the nav. Entitlement is unaffected — the destination applies the
   * same fail-closed gate every other surface does.
   */
  const activatePersona = (p: (typeof PERSONA_WORKSPACES)[number]) => {
    setPersona(p.id as PersonaTag);
    setOpen(false);
    navigate(p.defaultLandingRoute);
  };

  React.useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const Icon = MODE_META[mode].icon;
  const subLabel =
    mode === 'persona'
      ? PERSONA_WORKSPACES.find((p) => p.id === persona)?.displayName ?? 'Pick persona'
      : mode === 'industry'
      ? INDUSTRY_BUNDLES.find((b) => b.id === industry)?.displayName ?? 'Pick industry'
      : MODE_META[mode].label;

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        // `whitespace-normal` here is what made "ISA-95 layers" wrap to THREE
        // lines inside a one-line chip at 1280px (UX-A6). A compact chip has no
        // wrap to give: it either fits on one line or its label is hidden by the
        // breakpoint below. `shrink-0` keeps the flex row from squeezing it into
        // wrapping again from the outside.
        className="h-auto whitespace-nowrap shrink-0 [&_svg]:size-3.5 hover:text-foreground flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium hover:bg-muted"
        data-testid="nav-mode-switcher"
      >
        <Icon className="h-3.5 w-3.5" />
        <span className="hidden md:inline">{subLabel}</span>
        <ChevronDown className="h-3 w-3 opacity-60" />
      </Button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 w-56 rounded-md border border-border bg-popover p-1 text-xs shadow-md"
        >
          <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Navigation mode
          </div>
          {(Object.entries(MODE_META) as Array<[NavMode, typeof MODE_META[NavMode]]>).map(
            ([m, meta]) => {
              const M = meta.icon;
              return (
                <Button
                  key={m}
                  type="button"
                  variant="ghost"
                  role="menuitemradio"
                  aria-checked={mode === m}
                  onClick={() => setMode(m)}
                  className={`h-auto whitespace-normal justify-start text-xs font-normal [&_svg]:size-3.5 hover:text-foreground flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-muted ${
                    mode === m ? 'bg-muted/60 font-semibold' : ''
                  }`}
                >
                  <M className="h-3.5 w-3.5" />
                  {meta.label}
                </Button>
              );
            }
          )}

          {mode === 'persona' && (
            <>
              <div className="mt-1 border-t border-border px-2 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Active persona
              </div>
              <div className="max-h-48 overflow-y-auto">
                {PERSONA_WORKSPACES.map((p) => (
                  <Button
                    key={p.id}
                    type="button"
                    variant="ghost"
                    onClick={() => activatePersona(p)}
                    className={`h-auto whitespace-normal justify-start text-xs font-normal hover:text-foreground block w-full rounded px-2 py-1 text-left hover:bg-muted ${
                      persona === p.id ? 'bg-muted/60 font-semibold' : ''
                    }`}
                  >
                    {p.displayName}
                  </Button>
                ))}
              </div>
            </>
          )}

          {mode === 'industry' && (
            <>
              <div className="mt-1 border-t border-border px-2 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Active industry
              </div>
              <div className="max-h-48 overflow-y-auto">
                {INDUSTRY_BUNDLES.map((b) => (
                  <Button
                    key={b.id}
                    type="button"
                    variant="ghost"
                    onClick={() => setIndustry(b.id as IndustryTag)}
                    className={`h-auto whitespace-normal justify-start text-xs font-normal hover:text-foreground block w-full rounded px-2 py-1 text-left hover:bg-muted ${
                      industry === b.id ? 'bg-muted/60 font-semibold' : ''
                    }`}
                  >
                    {b.displayName}
                  </Button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
